import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiOrigin } from '../utils/apiBase';

const AuthContext = createContext();
const TOKEN_KEY = 'token';
const ADMIN_IMPERSONATION_TOKEN_KEY = 'adminImpersonationToken';

function notifyExtensionAuth(token, user) {
  if (typeof window === 'undefined') return;
  const apiOrigin = getApiOrigin() || window.location.origin;
  window.__RTC_API_ORIGIN = apiOrigin;
  window.postMessage({
    source: 'rtc-lab',
    type: 'RTC_AUTH_SYNC',
    token: token || null,
    user: user || null,
    apiOrigin
  }, '*');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get('/api/auth/me');
      setUser(res.data);
      notifyExtensionAuth(localStorage.getItem(TOKEN_KEY), res.data);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ADMIN_IMPERSONATION_TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      notifyExtensionAuth(null, null);
    } finally {
      setLoading(false);
    }
  };

  const applyAuthPayload = (payload) => {
    localStorage.setItem(TOKEN_KEY, payload.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${payload.token}`;
    setUser(payload.user);
    notifyExtensionAuth(payload.token, payload.user);
  };

  const login = async (username, password, role) => {
    const res = await axios.post('/api/auth/login', {
      username,
      password,
      role
    });
    localStorage.removeItem(ADMIN_IMPERSONATION_TOKEN_KEY);
    applyAuthPayload(res.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_IMPERSONATION_TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    notifyExtensionAuth(null, null);
  };

  const impersonateUser = async (targetUserId) => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (!currentToken) throw new Error('No auth token found');

    if (!localStorage.getItem(ADMIN_IMPERSONATION_TOKEN_KEY)) {
      localStorage.setItem(ADMIN_IMPERSONATION_TOKEN_KEY, currentToken);
    }

    try {
      const res = await axios.post(`/api/auth/impersonate/${targetUserId}`);
      applyAuthPayload(res.data);
      return res.data;
    } catch (error) {
      if (currentToken) {
        localStorage.setItem(TOKEN_KEY, currentToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      }
      throw error;
    }
  };

  const exitImpersonation = async () => {
    const res = await axios.post('/api/auth/impersonate/exit');
    localStorage.removeItem(ADMIN_IMPERSONATION_TOKEN_KEY);
    applyAuthPayload(res.data);
    return res.data;
  };

  const isImpersonating = Boolean(
    user?.impersonation?.active || localStorage.getItem(ADMIN_IMPERSONATION_TOKEN_KEY)
  );

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      impersonateUser,
      exitImpersonation,
      isImpersonating
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

